import { ReactNode } from 'react';
import { Flipped, Flipper } from 'react-flip-toolkit';
import styled, { css } from 'styled-components';

import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

const itemWidth = 185;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  min-height: 128px;
  background: rgba(var(--c-rgb-black), 0.4);
`;

const LoadingIndicatorContainer = styled.div`
  position: sticky;
  top: 50%;
  transform: translateY(-50%);
`;

export const GridOverlayLoadingIndicator = () => {
  return (
    <LoadingOverlay>
      <LoadingIndicatorContainer>
        <LoadingIndicator />
      </LoadingIndicatorContainer>
    </LoadingOverlay>
  );
};

const gridCss = css<{ $columns?: number }>`
  display: grid;
  gap: var(--double-unit);
  grid-template-columns: 1fr;
  /* only for mobile */
  &.twoColumnsOnMobile {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media ${tabletAndAbove} {
    grid-template-columns: repeat(auto-fill, minmax(${itemWidth}px, 1fr));

    /* the following rules are for where the grid is full width */
    /* it ensures that each line has the same number of cards regardless of the screen size */
    ${props =>
      props.$columns &&
      css`
        grid-template-columns: repeat(${props.$columns}, minmax(0, 1fr));
      `}
  }
`;

export const AnimatedGrid = styled(Flipper)<{ $columns?: number }>`
  ${gridCss}
`;

export const Grid = styled.div<{ $columns?: number }>`
  ${gridCss}
`;

const onAppear = (el: HTMLElement) => {
  el.animate?.([{ opacity: 0 }, { opacity: 1 }], {
    duration: 400,
  });
  el.style.opacity = '1';
};

export const AnimatedGridItem = ({
  children,
  flipId,
  onMouseEnter,
  onMouseLeave,
}: {
  children: ReactNode;
  flipId: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  return (
    <Flipped onAppear={onAppear} flipId={flipId}>
      <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {children}
      </div>
    </Flipped>
  );
};

export const GridItem = ({
  children,
  onMouseEnter,
  onMouseLeave,
}: {
  children: ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {children}
    </div>
  );
};
